/**
 * 2D 캔버스 컨텍스트에 둥근 사각형 경로를 생성하는 헬퍼 함수
 */
function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

const PHASES = [
  { id: 'SETUP', label: '준비' },
  { id: 'DRAW', label: '당김' },
  { id: 'ANCHOR', label: '조준' },
  { id: 'RELEASE', label: '발사' },
];

/**
 * 영상 녹화 및 스냅샷 캔버스에 태블릿 기준 자세 분석 HUD(각도/타이머 박스 및 상단 상태바)를 렌더링합니다.
 * @param {CanvasRenderingContext2D} ctx - 2D 캔버스 컨텍스트
 * @param {Object} params - 렌더링 파라미터
 */
export function drawPoseHud(ctx, {
  width,
  height,
  angles = {},
  phaseData = {},
  fps = 30,
  dominantHand = 'right',
  isPaused = false,
  autoClipEnabled = false,
  autoClipStatus = 'IDLE',
  shotCount = 0,
}) {
  if (!ctx || width <= 0 || height <= 0) return;

  const scale = Math.max(0.55, Math.min(width / 1280, height / 720));
  const isLeft = dominantHand === 'left';
  const currentPhase = phaseData?.phase || 'SETUP';

  ctx.save();

  // -------------------------------------------------------------
  // 1. 상단 상태 바 & 사격 페이즈 표시
  // -------------------------------------------------------------
  const topBarY = 16 * scale;
  const topBarHeight = 32 * scale;
  let topBarLeftX = 16 * scale;

  // [상단 좌측 1] FPS 뱃지
  const fpsText = isPaused ? '일시 정지됨' : `${fps || 30} FPS`;
  ctx.font = `bold ${Math.round(11 * scale)}px Outfit, sans-serif`;
  const fpsTextWidth = ctx.measureText(fpsText).width;
  const fpsPillWidth = fpsTextWidth + 20 * scale;

  ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
  ctx.lineWidth = 1 * scale;
  roundRect(ctx, topBarLeftX, topBarY, fpsPillWidth, topBarHeight, 16 * scale);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = '#e2e8f0';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(fpsText, topBarLeftX + fpsPillWidth / 2, topBarY + topBarHeight / 2);
  topBarLeftX += fpsPillWidth + 8 * scale;

  // [상단 좌측 2] 기준손 뱃지
  const handText = isLeft ? '왼쪽 기준' : '오른쪽 기준';
  ctx.font = `bold ${Math.round(11 * scale)}px Outfit, sans-serif`;
  const handTextWidth = ctx.measureText(handText).width;
  const handPillWidth = handTextWidth + 20 * scale;

  ctx.fillStyle = 'rgba(30, 41, 59, 0.88)';
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.5)';
  ctx.lineWidth = 1 * scale;
  roundRect(ctx, topBarLeftX, topBarY, handPillWidth, topBarHeight, 16 * scale);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = '#38bdf8';
  ctx.fillText(handText, topBarLeftX + handPillWidth / 2, topBarY + topBarHeight / 2);
  topBarLeftX += handPillWidth + 8 * scale;

  // [상단 좌측 3] 자동 컷팅/녹화 상태 뱃지 (활성화된 경우)
  if (autoClipEnabled) {
    let clipText = `AI 자동 컷팅 (${shotCount}발)`;
    let clipBg = 'rgba(14, 165, 233, 0.88)';
    if (autoClipStatus === 'RECORDING') {
      clipText = `Shot #${shotCount + 1} 녹화 중`;
      clipBg = 'rgba(220, 38, 38, 0.9)';
    } else if (autoClipStatus === 'SAVED') {
      clipText = `Shot #${shotCount} 저장 완료`;
      clipBg = 'rgba(16, 185, 129, 0.9)';
    }

    ctx.font = `bold ${Math.round(11 * scale)}px Outfit, sans-serif`;
    const clipTextWidth = ctx.measureText(clipText).width;
    const clipPillWidth = clipTextWidth + 24 * scale;

    ctx.fillStyle = clipBg;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    roundRect(ctx, topBarLeftX, topBarY, clipPillWidth, topBarHeight, 16 * scale);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.fillText(clipText, topBarLeftX + clipPillWidth / 2, topBarY + topBarHeight / 2);
  }

  // [상단 우측] 사격 페이즈 뱃지들 (준비, 당김, 조준, 발사)
  const phaseItemWidth = 56 * scale;
  const phaseGap = 4 * scale;
  const totalPhasesWidth = PHASES.length * phaseItemWidth + (PHASES.length - 1) * phaseGap + 12 * scale;
  const phasesStartX = width - 16 * scale - totalPhasesWidth;

  ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
  roundRect(ctx, phasesStartX, topBarY, totalPhasesWidth, topBarHeight, 16 * scale);
  ctx.fill();
  ctx.stroke();

  let phaseBtnX = phasesStartX + 6 * scale;
  const phaseBtnY = topBarY + 4 * scale;
  const phaseBtnHeight = topBarHeight - 8 * scale;

  PHASES.forEach((p) => {
    const isActive = currentPhase === p.id;
    if (isActive) {
      ctx.fillStyle = '#2563eb';
      roundRect(ctx, phaseBtnX, phaseBtnY, phaseItemWidth, phaseBtnHeight, 12 * scale);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
    } else {
      ctx.fillStyle = '#94a3b8';
    }
    ctx.font = `bold ${Math.round(11 * scale)}px Outfit, sans-serif`;
    ctx.fillText(p.label, phaseBtnX + phaseItemWidth / 2, phaseBtnY + phaseBtnHeight / 2);
    phaseBtnX += phaseItemWidth + phaseGap;
  });

  // -------------------------------------------------------------
  // 2. 하단 태블릿 기준 3열 2행 메트릭 패널
  // -------------------------------------------------------------
  const metrics = [
    // 1행
    {
      title: '받침 팔',
      sub: isLeft ? '14-12-24' : '13-11-23',
      val: `${angles?.angleSupportArm ?? 0}°`,
      color: '#38bdf8',
    },
    {
      title: '당김 팔',
      sub: isLeft ? '13-11-23' : '14-12-24',
      val: `${angles?.angleDrawArm ?? 0}°`,
      color: '#f97316',
    },
    {
      title: '앵커 각',
      sub: isLeft ? '11-13-15' : '12-14-16',
      val: `${angles?.drawElbowAngle || 0}°`,
      color: '#fbbf24',
    },
    // 2행
    {
      title: '지탱 각',
      sub: isLeft ? '12-14-16' : '11-13-15',
      val: `${angles?.bowArmAngle || 0}°`,
      color: '#34d399',
    },
    {
      title: '상체 수직',
      sub: '',
      val: `${angles?.torsoAngle || 90}°`,
      color: '#c084fc',
    },
    {
      title: '조준 시간',
      sub: '',
      val: phaseData?.anchorDuration > 0 ? `${phaseData.anchorDuration}s` : '-',
      color: '#facc15',
    },
  ];

  const cols = 3;
  const rows = 2;
  const panelMargin = 16 * scale;
  const gridGap = 8 * scale;
  const boxHeight = 52 * scale;
  const totalGridHeight = rows * boxHeight + (rows - 1) * gridGap;
  const gridY = height - panelMargin - totalGridHeight;
  const totalGridWidth = Math.min(width - panelMargin * 2, 760 * scale);
  const gridX = panelMargin; // 좌측 하단 배치
  const boxWidth = (totalGridWidth - (cols - 1) * gridGap) / cols;

  metrics.forEach((m, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const bx = gridX + col * (boxWidth + gridGap);
    const by = gridY + row * (boxHeight + gridGap);

    // 반투명 다크 카드 배경
    ctx.fillStyle = 'rgba(15, 23, 42, 0.86)';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.lineWidth = 1 * scale;
    roundRect(ctx, bx, by, boxWidth, boxHeight, 8 * scale);
    ctx.fill();
    ctx.stroke();

    // 좌측 컬러 바
    ctx.fillStyle = m.color;
    roundRect(ctx, bx, by, 4 * scale, boxHeight, 4 * scale);
    ctx.fill();

    // 컬러 인디케이터 점
    const dotX = bx + 12 * scale;
    const dotY = by + 16 * scale;
    ctx.beginPath();
    ctx.arc(dotX, dotY, 3.5 * scale, 0, 2 * Math.PI);
    ctx.fillStyle = m.color;
    ctx.fill();

    // 타이틀 텍스트
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = `600 ${Math.round(11 * scale)}px Outfit, sans-serif`;
    ctx.fillStyle = '#94a3b8';
    ctx.fillText(m.title, dotX + 8 * scale, dotY);

    // 관절 인덱스 서브 텍스트
    if (m.sub) {
      const titleWidth = ctx.measureText(m.title).width;
      ctx.font = `${Math.round(9 * scale)}px monospace`;
      ctx.fillStyle = '#64748b';
      ctx.fillText(m.sub, dotX + 8 * scale + titleWidth + 6 * scale, dotY);
    }

    // 메인 값 (각도/시간)
    ctx.font = `800 ${Math.round(18 * scale)}px Outfit, sans-serif`;
    ctx.fillStyle = m.color;
    ctx.fillText(m.val, bx + 12 * scale, by + 37 * scale);
  });

  ctx.restore();
}
